/**
 * Date utilities for the web app
 */

/**
 * Get today's date in YYYY-MM-DD format using user's timezone
 */
export function getTodayDate(timezone?: string): string {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(new Date());
}

/**
 * Get user's timezone
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format a date for display
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(d);
}

/**
 * Get a friendly greeting based on time of day
 */
export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 5) return 'Hi there, night owl';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  if (hour < 21) return 'Good evening';
  return 'Hi there, night owl';
}

/**
 * Get a personalized sub-message based on context
 * Human, warm, varied - feels like a real companion
 */
export function getPersonalizedMessage(options: {
  hasCheckedIn: boolean;
  streak?: number;
  mood?: number;
  energy?: number;
  dayOfWeek?: number;
  isFirstVisitToday?: boolean;
}): string {
  const { hasCheckedIn, streak = 0, mood, energy, dayOfWeek = new Date().getDay() } = options;
  const hour = new Date().getHours();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  // Use date-based seed for daily variety (same message all day, changes tomorrow)
  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  const pick = <T>(arr: T[]): T => arr[seed % arr.length];

  // Not checked in yet - warm invitations that feel real
  if (!hasCheckedIn) {
    if (hour < 6) {
      return pick([
        "Up with the quiet hours",
        "The world is still asleep",
        "A rare peaceful moment",
      ]);
    }
    if (hour < 9) {
      return pick([
        "Ready when you are",
        "Let's see how you're doing",
        "Coffee first, then check in",
        "New day, fresh start",
      ]);
    }
    if (hour < 12) {
      return pick([
        "Quick check-in waiting for you",
        "30 seconds for yourself?",
        "Let's see where you're at",
      ]);
    }
    if (hour < 17) {
      return pick([
        "How's the day going?",
        "Take a beat with me",
        "Pause for just a moment",
      ]);
    }
    if (hour < 21) {
      return pick([
        "How'd today go?",
        "Let's check in before evening",
        "Quick moment to reflect",
      ]);
    }
    return pick([
      "One last check-in?",
      "Let's wind down together",
      "Quiet moment before bed",
    ]);
  }

  // High streak messages - celebrate authentically
  if (streak >= 14) {
    return pick([
      "Two weeks strong",
      "This is becoming second nature",
      "You're making this a habit",
    ]);
  }
  if (streak >= 7) {
    return pick([
      "A whole week of showing up",
      "Seven days of choosing yourself",
      "That's real consistency",
    ]);
  }
  if (streak >= 3) {
    return pick([
      "You're building something here",
      "Three days and counting",
      "Momentum feels good, right?",
    ]);
  }

  // Low mood - genuinely supportive, not preachy
  if (mood && mood <= 2) {
    return pick([
      "Rough one. We're here.",
      "It's okay to not be okay",
      "Just getting through counts",
      "Gentleness today",
    ]);
  }

  // Low energy - understanding
  if (energy && energy <= 1) {
    return pick([
      "Running on fumes is hard",
      "Low battery is real",
      "Keep it easy today",
      "Rest counts as progress",
    ]);
  }

  // Good mood - share the joy naturally
  if (mood && mood >= 4) {
    return pick([
      "Look at you go",
      "Good vibes today",
      "Love to see it",
      "You're showing up strong",
    ]);
  }

  // Weekend vibes
  if (isWeekend) {
    if (hour < 12) {
      return pick([
        "Weekend morning mode",
        "Hopefully slower today",
        "Soak it in",
      ]);
    }
    return pick([
      "Make space for rest",
      "Weekend energy",
      "Recharge mode on",
    ]);
  }

  // Weekday defaults by time
  if (hour < 12) {
    return pick([
      "You're doing the thing",
      "One step at a time",
      "Today's got potential",
      "Here for whatever comes",
    ]);
  }
  if (hour < 17) {
    return pick([
      "Midday check complete",
      "Keep your pace",
      "Steady as you go",
    ]);
  }
  if (hour < 21) {
    return pick([
      "Almost through the day",
      "Home stretch",
      "Evening's coming",
    ]);
  }
  return pick([
    "Winding down soon",
    "Rest is coming",
    "You made it through",
  ]);
}

/**
 * Get the local date string (YYYY-MM-DD) from a Date object
 * Uses the user's local timezone for accurate day boundaries
 */
export function getLocalDateString(date: Date, timezone?: string): string {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
}

/**
 * Format minutes as duration string
 */
export function formatMinutes(minutes: number): string {
  if (minutes === 1) return '1 min';
  return `${minutes} min`;
}

/**
 * Check if a value is a valid date
 * Handles Date objects, strings, numbers, Firestore Timestamps, null, undefined
 */
export function isValidDate(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  
  // Handle Firestore Timestamp objects
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const timestamp = value as { toDate: () => Date };
    const date = timestamp.toDate();
    return !isNaN(date.getTime());
  }
  
  // Handle Date objects
  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }
  
  // Handle strings and numbers
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return !isNaN(date.getTime());
  }
  
  return false;
}

/**
 * Safely convert a value to a Date object
 * Returns null if the value cannot be converted to a valid date
 */
export function toSafeDate(value: unknown): Date | null {
  if (value === null || value === undefined) return null;
  
  // Handle Firestore Timestamp objects
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const timestamp = value as { toDate: () => Date };
    const date = timestamp.toDate();
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Handle Date objects
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  
  // Handle strings and numbers
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  
  return null;
}

/**
 * Format a document date with robust fallback
 * Returns a user-friendly string or fallback text for invalid/missing dates
 *
 * @param value - Primary date value to format
 * @param options.format - Date format: 'short', 'medium', or 'long'
 * @param options.fallback - Text to show if both value and fallbackDate are invalid
 * @param options.fallbackDate - Secondary date to use if primary is invalid (e.g., createdAt)
 */
export function formatDocumentDate(
  value: unknown,
  options?: {
    format?: 'short' | 'medium' | 'long';
    fallback?: string;
    fallbackDate?: unknown;
  }
): string {
  const { format = 'medium', fallback = 'Date unavailable', fallbackDate } = options || {};

  // Try primary date first, then fallback date
  let date = toSafeDate(value);
  if (!date && fallbackDate !== undefined) {
    date = toSafeDate(fallbackDate);
  }
  if (!date) return fallback;

  try {
    switch (format) {
      case 'short':
        return new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
        }).format(date);
      case 'long':
        return new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }).format(date);
      case 'medium':
      default:
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }).format(date);
    }
  } catch {
    return fallback;
  }
}

/**
 * Get a date N days ago in YYYY-MM-DD format
 */
export function getDateDaysAgo(daysAgo: number, timezone?: string): string {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
}

/**
 * Calculate time since an event in minutes (internal helper)
 */
function getMinutesSince(date: Date): number {
  const now = new Date();
  return Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
}

/**
 * Format "time since" for baby tracker summary
 * e.g., "2h 15m ago" or "45m ago"
 */
export function formatTimeSince(date: Date): string {
  const mins = getMinutesSince(date);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (remainingMins === 0) return `${hours}h ago`;
  return `${hours}h ${remainingMins}m ago`;
}

/**
 * Format a smart date/time for activity logs
 * Uses context-aware formatting for best UX:
 * - Today: "2:30 PM"
 * - Yesterday: "Yesterday, 2:30 PM"
 * - This week: "Mon, 2:30 PM"
 * - Older: "Jan 15, 2:30 PM"
 */
export function formatSmartDateTime(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const eventDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const timeStr = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  // Today - just show time
  if (eventDate.getTime() === today.getTime()) {
    return timeStr;
  }

  // Yesterday
  if (eventDate.getTime() === yesterday.getTime()) {
    return `Yesterday, ${timeStr}`;
  }

  // Within last 7 days - show day name
  if (eventDate.getTime() > oneWeekAgo.getTime()) {
    const dayName = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
    return `${dayName}, ${timeStr}`;
  }

  // Older - show month and day
  const dateStr = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date);
  return `${dateStr}, ${timeStr}`;
}
